import React, { useEffect, useMemo, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeftIcon, HomeIcon } from '@heroicons/react/24/outline'
import { useNavigationHistory } from '../../context/NavigationHistoryContext'
import { entityAPI } from '../../services/api'
import { useAuth } from '../../context/AuthContext'
import { sessionStorage as sessionStore } from '../../utils/storage'
import { toast } from '../../services/toastService'
import SEOHead from '../../components/SEOHead'
import { 
  generateEntitySchema, 
  generateBreadcrumbSchema, 
  formatEntityForSEO,
  generatePageTitle 
} from '../../utils/seo'
import { useTranslation } from 'react-i18next'
import EntityDetailMap from '../../pages/EntityDetailMap'
import ErrorBoundary from '../../components/ErrorBoundary'
import { useMapSpot } from '../../context/MapSpotContext'
import { useAsyncOperation } from '../../hooks/useAsyncOperation'
import { useImmersiveTabs } from '../../hooks/useImmersiveTabs'
import { useUnreadChannels } from '../../hooks/useUnreadChannels'
import GlassmorphButton from '../../components/ui/GlassmorphButton'
import { useVendorStatus } from '../../hooks/useVendorStatus'
import { useImmersiveChannelState } from '../../hooks/useImmersiveChannelState'
import { useImmersiveTabBar } from '../../hooks/useImmersiveTabBar'
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout'

export default function ImmersiveEntityDetail() {
  const { id } = useParams()
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { isAuthenticated, user } = useAuth()
  const { activateSpotMode } = useMapSpot()
  const { loading: isLoading, error, data: entity, execute: executeEntity } = useAsyncOperation()
  const { loading: isSightingsLoading, data: sightings, execute: executeSightings } = useAsyncOperation()
  // Track unread channel count for admin users
  const { unreadCount: unreadChannelCount, refreshUnreadCount } = useUnreadChannels(isAuthenticated, user)
  const { isVendor, vendorEntities } = useVendorStatus()
  const { isDesktop } = useResponsiveLayout()

  // Use custom hook for tab management with entity-specific panel cycling
  const {
    activeTab,
    setActiveTab,
    panelState,
    handleTabClick
  } = useImmersiveTabs({
    defaultTab: 'search',
    defaultPanelState: 'expanded',
    pageName: 'entity_detail',
    entityTabState: 'expanded', // Entity detail starts fully expanded
    panelCycle: ['expanded', 'semi', 'minimized'],
    isAuthenticated
  })

  // Channel navigation state — shared with ImmersiveHome via useImmersiveChannelState
  const {
    initialChannelId,
    setInitialChannelId,
    claimPrefill,
    setClaimPrefill,
    handleTabChangeWithChannel,
    handleChannelSelected,
  } = useImmersiveChannelState({ handleTabClick, userEmail: user?.email, switchToContactOnSelect: true })

  // Clear initialChannelId when user changes (logout/login as different user)
  useEffect(() => {
    setInitialChannelId(null)
  }, [user?.id])

  // Always land on the entity info tab (search) when navigating to a new entity
  // Without this, navigating entity→entity would preserve whatever tab was active
  useEffect(() => {
    setActiveTab('search')
  }, [id])

  // In-app navigation history stack — knows whether a safe prior page exists
  const { canGoBack, goBack } = useNavigationHistory()

  // Back navigation: OAuth edge-case first, then defer to in-app stack
  const handleBackClick = useCallback(() => {
    const justCompletedOAuth = sessionStore.get('isOAuthFlow') === 'true'
    sessionStore.remove('isOAuthFlow')
    if (justCompletedOAuth) {
      navigate('/', { replace: true })
      return
    }
    goBack()
  }, [goBack, navigate])

  // Back button: arrow when there's in-app history, home icon on direct links
  const backButton = useMemo(() => (
    <GlassmorphButton
      icon={canGoBack ? ArrowLeftIcon : HomeIcon}
      label={canGoBack ? 'Go back' : 'Home'}
      onClick={handleBackClick}
      variant="icon"
    />
  ), [handleBackClick, canGoBack])

  // Memoize the tabs element - includes i18n.language to re-render on language change
  const immersiveTabsElement = useImmersiveTabBar({
    activeTab,
    panelState,
    handleTabClick,
    isAuthenticated,
    unreadChannelCount,
    isVendor,
    language: i18n.language,
    prependElement: backButton,
  })

  useEffect(() => {
    fetchEntity()
    fetchSightings()
  }, [id])

  const fetchEntity = async () => {
    await executeEntity(
      async () => {
        const response = await entityAPI.getEntity(id)
        return response.data
      },
      { errorMessage: 'Failed to load entity details' }
    )
  }

  const fetchSightings = async () => {
    await executeSightings(
      async () => {
        const response = await entityAPI.getEntityLocations(id, { page: 1, limit: 20 })
        return response.data.data || []
      },
      { silent: true }
    )
  }

  // Only admins and active vendors for this specific entity can edit schedule/offerings
  const entityId = entity?.id || id
  const canEdit = isAuthenticated && !!entity && (
    user?.is_admin === true ||
    vendorEntities.some(ve => ve.id === entityId)
  )

  const handleReportSighting = () => {
    if (entity) {
      let location = null
      if (sightings && sightings.length > 0) {
        const latestSighting = sightings[0]
        location = {
          lat: latestSighting.latitude,
          lng: latestSighting.longitude
        }
      } else if (entity.current_latitude && entity.current_longitude) {
        location = {
          lat: entity.current_latitude,
          lng: entity.current_longitude
        }
      }
      const entityWithId = { ...entity, id: entity.id || id }
      activateSpotMode(() => fetchSightings(), entityWithId, location)
    }
  }

  const handleShareSighting = async (sighting) => {
    const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${sighting.latitude},${sighting.longitude}`
    
    try {
      await navigator.clipboard.writeText(googleMapsUrl)
      toast.success(t('toast.success.linkCopied'))
    } catch (error) {
      // Error already shown to user via toast
      toast.error(t('toast.error.linkCopyFailed'))
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="mt-4 text-gray-600">{t('app.loading', 'Loading...')}</p>
        </div>
      </div>
    )
  }

  if (error || !entity) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="bg-white border border-red-200 rounded-2xl shadow-lg p-8 max-w-sm w-full mx-4 text-center">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
            </svg>
          </div>
          <p className="text-gray-800 font-medium mb-1">{t('entityDetail.loadError', 'Could not load entity')}</p>
          <p className="text-gray-500 text-sm mb-5">{error || t('entityDetail.notFound', 'This entity could not be found.')}</p>
          <button
            onClick={handleBackClick}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            {t('common.goBack', 'Go Back')}
          </button>
        </div>
      </div>
    )
  }

  // Generate SEO data for entity
  const seoData = entity ? formatEntityForSEO(entity) : null
  const entitySchema = entity ? generateEntitySchema(entity) : null
  const breadcrumbs = entity ? generateBreadcrumbSchema([
    { name: 'Home', path: '/' },
    { name: entity.category || 'Vendors', path: '/' },
    { name: entity.name, path: `/entities/${entity.id}` }
  ]) : null

  return (
    <>
      {/* SEO Meta Tags and Structured Data */}
      {entity && seoData && (
        <SEOHead
          title={generatePageTitle(entity.name)}
          description={seoData.description}
          keywords={seoData.keywords}
          canonical={seoData.canonical}
          image={`${import.meta.env.VITE_API_URL || ''}/api/entities/${id}/og-image.png`}
          type="place"
          structuredData={entitySchema}
          breadcrumbs={breadcrumbs}
        />
      )}
      
      <div className="relative w-screen h-screen overflow-hidden">
        {/* Full-screen Map — left edge tracks panel width */}
        <div
          className="absolute top-0 right-0 bottom-0 z-0 transition-[left] duration-300 ease-out"
          style={{ left: isDesktop ? (panelState === 'minimized' ? '64px' : '360px') : 0 }}
        >
        <ErrorBoundary>
        <EntityDetailMap 
          key={`entity-map-${id}`}
          componentSource="ImmersiveEntityDetail"
          entityId={id}
          entityName={entity?.name}
          entityLocation={
            // Prioritize latest sighting location over entity's general location
            sightings && sightings.length > 0 ? {
              latitude: sightings[0].latitude,
              longitude: sightings[0].longitude
            } : entity && (entity.currentLatitude || entity.current_latitude) && (entity.currentLongitude || entity.current_longitude) ? {
              latitude: entity.currentLatitude || entity.current_latitude,
              longitude: entity.currentLongitude || entity.current_longitude
            } : null
          }
          entityDetails={entity}
          canEditEntity={canEdit}
          isImmersiveMode={true}
          activeTab={activeTab}
          onTabChange={handleTabChangeWithChannel}
          panelState={panelState}
          onPanelStateChange={(newState) => handleTabChangeWithChannel(activeTab, newState)}
          immersiveTabs={immersiveTabsElement}
          initialChannelId={initialChannelId}
          onChannelSelected={handleChannelSelected}
          onUnreadCountChange={refreshUnreadCount}
          claimPrefill={claimPrefill}
          onClaimPrefillConsumed={() => setClaimPrefill(null)}
        />
        </ErrorBoundary>
      </div>
    </div>
    </>
  )
}
